<template>
  <QPageContainer>
    <QPage class="create-demand-page">
      <QHeader class="bg-white text-primary shadow-soft">
        <QToolbar>
          <QToolbarTitle>
            {{ $t("ETAB.PAGES.SEARCH_DASHBOARD.@TITLE") }}
          </QToolbarTitle>
        </QToolbar>
      </QHeader>
      <section class="create-demand-page-content flex justify-center">
        <ExtraTipbox v-if="banner?.message" class="q-ma-md q-mb-lg">
          <section class="q-pa-sm">
            <p class="q-mb-none">
              {{ $filters.whitespacedPunctuation(banner.message) }}
            </p>
          </section>
        </ExtraTipbox>
        <CreateDemand :active-contracts="activeContracts" @on-click-action="onClickAction" />
      </section>

      <MarketingCampaignComponent />
    </QPage>
  </QPageContainer>
</template>

<script>
  import { StatusBar, isNative } from "@core/plugins/mobile";
  import { CreationDemandService } from "@core/services";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { DataLayerManager } from "@core/managers/datalayer-manager";
  import { ActiveContractsModel } from "@core/models";
  import { APP_ETAB, CREATION_ACTIVE_CONTRACT, DEFAULT_STEP, FEATURE_TOGGLES } from "@core/constants";
  import { RouterHelpers } from "@core/helpers";
  import { ETAB_ROUTES, META_PROGRESS } from "@etab/config";
  import { BatchManager } from "@core/plugins/batch/batch.manager";
  import { ACTION_BATCH_GET_USER_DATA, CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE, GETTER_BATCH_GET_USER_DATA } from "@core/store/types";
  import { useFeatureTogglesStore } from "@core/store/modules/featureToggles";
  import { useProfileStore } from "@etab/stores/profile";
  import { GETTER_PROFILE_GET } from "@etab/stores/types";
  import { mapStores } from "pinia";
  import { UserStorageManager } from "@core/managers/user-storage.manager";

  import CreateDemand from "@etab/components/dashboard/CreateDemand.component.vue";
  import MarketingCampaignComponent from "@etab/components/marketing-campaign/MarketingCampaign.component.vue";

  export default {
    components: {
      CreateDemand,
      MarketingCampaignComponent,
    },
    data() {
      return {
        activeContracts: [],
        banner: null,
      };
    },
    computed: {
      ...mapStores(useProfileStore, useFeatureTogglesStore),
      profile() {
        return this.profileStore[GETTER_PROFILE_GET];
      },
      shouldDisplayError() {
        return this.$route.name === ETAB_ROUTES.creation.error.name;
      },
      posthogDemandCreationEventsEnabled() {
        return this.featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.POSTHOG_ETAB_TEMPLATE_FUNNEL);
      },
    },
    async created() {
      StatusBar.setDefault();
      this.CREATION_ACTIVE_CONTRACT = CREATION_ACTIVE_CONTRACT;
      await this.init();
      this.getActiveContract();
    },
    methods: {
      async init() {
        try {
          this.$_startGlobalLoading();
          if (isNative) {
            await BatchManager.start({
              storeAction: ACTION_BATCH_GET_USER_DATA,
              storeGetter: GETTER_BATCH_GET_USER_DATA,
              env: APP_ETAB,
            });
          }
          const categoryEtab = this.profile.getCategoryEtab();
          const userProfileEntreprise = categoryEtab.getFamilyCategory().nom;

          DataLayerManager.setData(
            {
              page_type: "formulaire",
              formulaire: "creation_annonce",
              user_profile_entreprise: userProfileEntreprise,
              formulaire_step_number: DEFAULT_STEP,
            },
            "etablissement",
            StorageManager.getUserInfo("etablissement"),
          );
        } catch (error) {
          // suppress error, we don't care so much
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async createDemand(contract) {
        const contractName = contract.getName();
        try {
          this.$_startGlobalLoading();
          const data = await CreationDemandService.initTemplate(contractName);
          const templateId = data.id;
          const nextStepSlug = data.next_step;
          if (this.$posthog && this.posthogDemandCreationEventsEnabled) {
            let clientType = await UserStorageManager.getClientType();
            this.$posthog.capture("template_created", { $contract: contractName, $client_type: clientType });
          }
          this.$router
            .push({ name: ETAB_ROUTES.creation.funnel.step.slug.name, params: { templateId, stepSlug: nextStepSlug } })
            .catch((suppressedError) => {});
        } catch (error) {
          NotificationsManager.error();
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async getActiveContract() {
        try {
          this.$_startGlobalLoading();
          const data = await CreationDemandService.getActiveContract();
          this.banner = data.banner;
          this.activeContracts = data.contracts.map((contract) => new ActiveContractsModel(contract));
        } catch (error) {
          NotificationsManager.error();
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      setDataLayerWithContract(contractType) {
        setTimeout(() => {
          return DataLayerManager.setData(
            { page_type: "formulaire", formulaire: "creation_annonce", formulaire_step_number: this.getStepNumber(), creation_type: contractType },
            "etablissement",
            "etablissement",
          );
        }, 500);
      },
      getStepNumber() {
        const stepNumber = RouterHelpers.getRouteMeta(this.$route, META_PROGRESS);
        return stepNumber;
      },
      onClickAction(contract) {
        if (contract.isActivated()) {
          this.setDataLayerWithContract(contract.name);
          this.createDemand(contract);
        }
      },
    },
  };
</script>

<style lang="scss">
  .create-demand-page {
    .create-demand-page-content {
      max-width: 660px;
      margin: 0 auto;
      .extra-tipbox {
        overflow: visible;
        margin-top: 30px;
      }
    }
  }
</style>
